/**
 * HmWarehouseForm — wystawienie ORAZ edycja dokumentu magazynowego.
 *
 * Jeden ekran dla dwóch trybów, bo Symfonia przyjmuje przy edycji ten sam komplet pól co przy
 * wystawianiu — rozdzielenie na dwa komponenty oznaczałoby dwie kopie tej samej formatki, które
 * rozjechałyby się przy pierwszej zmianie.
 *
 * DLACZEGO EDYCJA JEST TYLKO TUTAJ: magazyn to jedyny obszar modułu, w którym WebAPI pozwala
 * poprawić istniejący dokument. Sprzedaż i kasa mają wyłącznie tworzenie — tam poprawka oznacza
 * korektę po stronie Symfonii.
 *
 * PUŁAPKA EDYCJI POZYCJI: Symfonia oczekuje PEŁNEJ listy, nie różnicy. Pozycja usunięta w
 * formularzu musi pojechać z flagą skreślenia — samo jej pominięcie zostawiłoby ją na dokumencie.
 * Dlatego skreślone wiersze trzymamy w stanie (wyszarzone), zamiast je wyrzucać.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  HmContractorListItem,
  HmListResponse,
  HmProductListItem,
  HmWarehouseDetail,
  HmWarehouseDictionaries,
  HmWarehouseIssueInput,
  HmWarehouseIssueResult,
  HmWarehouseKind,
} from '@saas/shared';
import { HM_WAREHOUSE_KINDS } from '@saas/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Plus, RotateCcw, Save, Send, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DatePicker } from '@/components/DatePicker';
import { Input } from '@/components/Input';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

interface PositionRow {
  key: string;
  /** Identyfikator pozycji w Symfonii — jest tylko przy edycji istniejącego wiersza. */
  positionId?: number;
  productCode: string;
  name: string;
  quantity: string;
  unit: string;
  value: string;
  /** Skreślona: zostaje w stanie, żeby pojechać do Symfonii z flagą usunięcia (patrz nagłówek). */
  deleted: boolean;
}

function emptyRow(): PositionRow {
  return {
    key: crypto.randomUUID(),
    productCode: '',
    name: '',
    quantity: '1',
    unit: '',
    value: '',
    deleted: false,
  };
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function num(value: string): number {
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

export function HmWarehouseForm({ mode }: { mode: 'create' | 'edit' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const companyId = useActiveCompanyScope().companyId;
  const editing = mode === 'edit';

  const [kind, setKind] = useState<HmWarehouseKind>('WZ');
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [series, setSeries] = useState('');
  const [warehouseCode, setWarehouseCode] = useState('');
  const [receivingWarehouseCode, setReceivingWarehouseCode] = useState('');
  const [withReceivingDocument, setWithReceivingDocument] = useState(false);
  const [registryCode, setRegistryCode] = useState('');
  const [contractorId, setContractorId] = useState<string | null>(null);
  const [issueDate, setIssueDate] = useState(today());
  const [operationDate, setOperationDate] = useState(today());
  const [description, setDescription] = useState('');
  const [note, setNote] = useState('');
  const [rows, setRows] = useState<PositionRow[]>([emptyRow()]);
  const [confirmIssue, setConfirmIssue] = useState(false);

  const q = `companyId=${companyId}`;
  const needsReceiving = kind === 'MMMinus';

  const dictionaries = useQuery({
    queryKey: ['handel-magazyn', 'warehouse-dictionaries', companyId, kind],
    enabled: !!companyId,
    retry: false,
    queryFn: async () =>
      (
        await api.get<HmWarehouseDictionaries>(
          `/handel-magazyn/warehouse-dictionaries?kind=${kind}&${q}`,
        )
      ).data,
  });

  const products = useQuery({
    queryKey: ['handel-magazyn', 'products', companyId],
    enabled: !!companyId,
    retry: false,
    queryFn: async () =>
      (await api.get<HmListResponse<HmProductListItem>>(`/handel-magazyn/products?${q}`)).data
        .items,
  });

  const contractors = useQuery({
    queryKey: ['handel-magazyn', 'contractors', companyId],
    enabled: !!companyId,
    retry: false,
    queryFn: async () =>
      (await api.get<HmListResponse<HmContractorListItem>>(`/handel-magazyn/contractors?${q}`)).data
        .items,
  });

  const seriesQuery = useQuery({
    queryKey: ['handel-magazyn', 'warehouse-series', companyId, documentTypeId],
    enabled: !!companyId && !!documentTypeId,
    retry: false,
    queryFn: async () =>
      (
        await api.get<{ items: { id: number; code: string }[] }>(
          `/handel-magazyn/warehouse-series?documentTypeId=${documentTypeId}&${q}`,
        )
      ).data.items,
  });

  /** Tryb edycji: wczytujemy dokument i przepisujemy go na formularz. */
  const existing = useQuery({
    queryKey: ['handel-magazyn', 'warehouse-document', id, companyId],
    enabled: editing && !!id && !!companyId,
    retry: false,
    queryFn: async () =>
      (await api.get<HmWarehouseDetail>(`/handel-magazyn/warehouse-documents/${id}?${q}`)).data,
  });

  useEffect(() => {
    const doc = existing.data;
    if (!doc) return;
    setDescription(doc.description ?? '');
    setNote(doc.note ?? '');
    setIssueDate(doc.issueDate?.slice(0, 10) ?? today());
    setOperationDate(doc.operationDate?.slice(0, 10) ?? today());
    setRows(
      doc.positions.map((p) => ({
        key: crypto.randomUUID(),
        positionId: p.id,
        productCode: p.productCode,
        name: p.description,
        quantity: p.quantity,
        unit: p.unit,
        value: p.netValue,
        deleted: false,
      })),
    );
  }, [existing.data]);

  const dict = dictionaries.data;

  useEffect(() => {
    setDocumentTypeId('');
    setSeries('');
    if (kind !== 'MMMinus') setReceivingWarehouseCode('');
  }, [kind]);

  useEffect(() => {
    setSeries('');
  }, [documentTypeId]);

  useEffect(() => {
    const items = seriesQuery.data;
    if (items?.length) setSeries((prev) => prev || (items[0]?.code ?? ''));
  }, [seriesQuery.data]);

  useEffect(() => {
    if (dict?.warehouses.length)
      setWarehouseCode((prev) => prev || (dict.warehouses[0]?.code ?? ''));
  }, [dict]);

  function setRow(key: string, patch: Partial<PositionRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function pickProduct(key: string, code: string | null) {
    const product = products.data?.find((p) => p.code === code);
    if (!product) {
      setRow(key, { productCode: '' });
      return;
    }
    setRow(key, { productCode: product.code, name: product.name, unit: product.unit ?? '' });
  }

  /**
   * Usunięcie wiersza: przy edycji istniejącej pozycji tylko ją SKREŚLAMY (pojedzie z flagą),
   * nowy wiersz można wyrzucić od razu — w Symfonii go nie ma.
   */
  function removeRow(row: PositionRow) {
    if (row.positionId != null) setRow(row.key, { deleted: true });
    else setRows((prev) => prev.filter((r) => r.key !== row.key));
  }

  const activeRows = useMemo(() => rows.filter((r) => !r.deleted), [rows]);

  function buildPayload(issue: boolean): HmWarehouseIssueInput {
    return {
      kind,
      documentTypeCode:
        dict?.documentTypes.find((d) => String(d.id) === documentTypeId)?.code ?? '',
      series,
      ...(warehouseCode ? { warehouseCode } : {}),
      ...(registryCode ? { warehouseRegistryCode: registryCode } : {}),
      ...(needsReceiving && receivingWarehouseCode ? { receivingWarehouseCode } : {}),
      ...(contractorId ? { contractorId: Number(contractorId) } : {}),
      ...(issueDate ? { issueDate } : {}),
      ...(operationDate ? { operationDate } : {}),
      ...(description ? { description } : {}),
      ...(note ? { note } : {}),
      positions: rows.map((r) => ({
        ...(r.positionId != null ? { positionId: r.positionId } : {}),
        productCode: r.productCode,
        quantity: num(r.quantity),
        ...(r.unit ? { unitOfMeasurement: r.unit } : {}),
        ...(r.name ? { name: r.name } : {}),
        ...(r.value.trim() ? { value: num(r.value) } : {}),
        ...(r.deleted ? { deleted: true } : {}),
      })),
      ...(editing ? {} : { issue }),
      // MM+ ma sens tylko przy ZATWIERDZANYM przesunięciu — przy buforze Symfonia go odmówi,
      // więc nie wysyłamy prośby, o której z góry wiadomo, że przepadnie.
      ...(!editing && needsReceiving && issue && withReceivingDocument
        ? { withReceivingDocument: true }
        : {}),
    };
  }

  const mutation = useMutation({
    mutationFn: async (issue: boolean) => {
      const payload = buildPayload(issue);
      const url = editing
        ? `/handel-magazyn/warehouse-documents/${id}?${q}`
        : `/handel-magazyn/warehouse-documents?${q}`;
      const res = editing
        ? await api.put<HmWarehouseIssueResult>(url, payload)
        : await api.post<HmWarehouseIssueResult>(url, payload);
      return res.data;
    },
    onSuccess: (doc) => {
      toast.show({
        variant: 'success',
        title: editing
          ? t('tenant.handelMagazyn.newWarehouse.toast.edited', { number: doc.documentNumber })
          : doc.inBuffer
            ? t('tenant.handelMagazyn.newWarehouse.toast.buffered', { number: doc.documentNumber })
            : t('tenant.handelMagazyn.newWarehouse.toast.issued', { number: doc.documentNumber }),
        // Numeru MM+ zwykle nie znamy — Symfonia potwierdza wystawienie pustą odpowiedzią.
        description: doc.receivingIssued
          ? doc.receivingDocument
            ? t('tenant.handelMagazyn.newWarehouse.toast.receivingIssued', {
                number: doc.receivingDocument.documentNumber,
              })
            : t('tenant.handelMagazyn.newWarehouse.toast.receivingIssuedNoNumber')
          : undefined,
      });
      // MM− już istnieje, więc to nie jest awaria zapisu — ale użytkownik MUSI wiedzieć, że
      // przesunięcie zostało otwarte i domknięcie po stronie przyjmującej jest na nim.
      if (doc.receivingError) {
        toast.show({
          variant: 'warning',
          title: t('tenant.handelMagazyn.newWarehouse.toast.receivingFailed'),
          description: doc.receivingError,
        });
      }
      navigate(`/handel-magazyn/magazyn/${doc.id}`);
    },
    onError: (err) => {
      const data = isAxiosError(err)
        ? (err.response?.data as
            | {
                message?: string;
                details?: { body?: string };
                fieldErrors?: { field: string; message: string }[];
              }
            | undefined)
        : undefined;
      const fieldErrors = (data?.fieldErrors ?? []).map((e) => e.message).join(' · ');
      toast.show({
        variant: 'error',
        title: t('tenant.handelMagazyn.newWarehouse.toast.failed'),
        description: fieldErrors || data?.details?.body || data?.message || undefined,
      });
    },
  });

  const canSubmit =
    !!companyId &&
    !!documentTypeId &&
    !!series &&
    (!needsReceiving || (!!receivingWarehouseCode && receivingWarehouseCode !== warehouseCode)) &&
    activeRows.length > 0 &&
    activeRows.every((r) => r.productCode && num(r.quantity) > 0) &&
    !mutation.isPending;

  if (!companyId) {
    return (
      <div className="p-4 text-sm text-text-secondary">{t('tenant.handelMagazyn.pickCompany')}</div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">
            {editing
              ? t('tenant.handelMagazyn.newWarehouse.editTitle', {
                  number: existing.data?.documentNumber ?? '',
                })
              : t('tenant.handelMagazyn.newWarehouse.title')}
          </h1>
          <p className="text-sm text-text-secondary">
            {editing
              ? t('tenant.handelMagazyn.newWarehouse.editSubtitle')
              : t('tenant.handelMagazyn.newWarehouse.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <Button
              variant="primary"
              leftIcon={<Save className="h-4 w-4" />}
              disabled={!canSubmit}
              loading={mutation.isPending}
              onClick={() => mutation.mutate(false)}
            >
              {t('common.save')}
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                leftIcon={<Save className="h-4 w-4" />}
                disabled={!canSubmit}
                loading={mutation.isPending && !confirmIssue}
                onClick={() => mutation.mutate(false)}
              >
                {t('tenant.handelMagazyn.newSale.saveToBuffer')}
              </Button>
              <Button
                variant="primary"
                leftIcon={<Send className="h-4 w-4" />}
                disabled={!canSubmit}
                onClick={() => setConfirmIssue(true)}
              >
                {t('tenant.handelMagazyn.newSale.issue')}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 rounded-md border border-border-primary bg-bg-secondary p-4 lg:grid-cols-2">
        <div className="space-y-3">
          <Select
            label={t('tenant.handelMagazyn.newWarehouse.kind')}
            value={kind}
            onChange={(e) => setKind(e.target.value as HmWarehouseKind)}
            disabled={editing}
            hint={editing ? t('tenant.handelMagazyn.newWarehouse.kindLocked') : undefined}
            options={HM_WAREHOUSE_KINDS.map((k) => ({
              value: k,
              label: t(`tenant.handelMagazyn.newWarehouse.kinds.${k}`),
            }))}
          />
          <Select
            label={t('tenant.handelMagazyn.newSale.documentType')}
            value={documentTypeId}
            onChange={(e) => setDocumentTypeId(e.target.value)}
            placeholder={t('tenant.handelMagazyn.newSale.choose')}
            options={(dict?.documentTypes ?? []).map((d) => ({
              value: String(d.id),
              label: `${d.code} — ${d.name}`,
            }))}
          />
          <Select
            label={t('tenant.handelMagazyn.newSale.series')}
            value={series}
            onChange={(e) => setSeries(e.target.value)}
            disabled={!documentTypeId || seriesQuery.isLoading}
            placeholder={t('tenant.handelMagazyn.newSale.chooseAfterType')}
            options={(seriesQuery.data ?? []).map((s) => ({ value: s.code, label: s.code }))}
          />
          <div>
            <span className="mb-1 block text-sm font-medium text-text-primary">
              {t('tenant.handelMagazyn.columns.contractor')}
            </span>
            <SearchableSelect
              value={contractorId}
              onChange={setContractorId}
              allowClear
              options={(contractors.data ?? []).map((c) => ({
                value: String(c.id),
                label: c.name,
                description: c.code,
              }))}
              placeholder={t('tenant.handelMagazyn.newSale.buyerPlaceholder')}
              data-testid="hm-warehouse-contractor"
            />
          </div>
        </div>

        <div className="space-y-3">
          <Select
            label={t('tenant.handelMagazyn.warehouse.detail.source')}
            value={warehouseCode}
            onChange={(e) => setWarehouseCode(e.target.value)}
            options={(dict?.warehouses ?? []).map((w) => ({ value: w.code, label: w.name }))}
          />
          {/* Magazyn przyjmujący istnieje TYLKO przy przesunięciu — przy innych charakterach
              Symfonia odrzuci dokument z tym polem. */}
          {needsReceiving && (
            <Select
              label={t('tenant.handelMagazyn.warehouse.detail.target')}
              value={receivingWarehouseCode}
              onChange={(e) => setReceivingWarehouseCode(e.target.value)}
              placeholder={t('tenant.handelMagazyn.newSale.choose')}
              hint={t('tenant.handelMagazyn.newWarehouse.receivingHint')}
              options={(dict?.warehouses ?? [])
                .filter((w) => w.code !== warehouseCode)
                .map((w) => ({ value: w.code, label: w.name }))}
            />
          )}
          {/* MM+ domyka przesunięcie w magazynie przyjmującym. Przy edycji nie ma sensu — dokument
              już istnieje, a MM+ wystawia się do świeżo zatwierdzonego MM−. */}
          {needsReceiving && !editing && (
            <Checkbox
              checked={withReceivingDocument}
              onChange={(e) => setWithReceivingDocument(e.target.checked)}
              label={t('tenant.handelMagazyn.newWarehouse.withReceiving')}
              hint={t('tenant.handelMagazyn.newWarehouse.withReceivingHint')}
              data-testid="hm-warehouse-with-receiving"
            />
          )}
          <Select
            label={t('tenant.handelMagazyn.newWarehouse.registry')}
            value={registryCode}
            onChange={(e) => setRegistryCode(e.target.value)}
            placeholder={t('tenant.handelMagazyn.newSale.choose')}
            options={(dict?.warehouseRegistries ?? []).map((r) => ({
              value: r.code,
              label: r.name,
            }))}
          />
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col text-sm text-text-secondary">
              {t('tenant.handelMagazyn.columns.issueDate')}
              <DatePicker value={issueDate} onChange={(v) => setIssueDate(v || '')} />
            </label>
            <label className="flex flex-col text-sm text-text-secondary">
              {t('tenant.handelMagazyn.warehouse.columns.operationDate')}
              <DatePicker value={operationDate} onChange={(v) => setOperationDate(v || '')} />
            </label>
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-md border border-border-primary bg-bg-secondary p-3">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">
            {t('tenant.handelMagazyn.detail.tabs.positions')}
          </h2>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setRows((prev) => [...prev, emptyRow()])}
          >
            {t('tenant.handelMagazyn.newSale.addPosition')}
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-border-primary text-left text-xs uppercase tracking-wide text-text-tertiary">
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.code')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.description')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.detail.pos.quantity')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.unit')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.columns.net')}
                </th>
                <th className="w-10 px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, index) => (
                <tr
                  key={r.key}
                  className={`border-b border-border-primary/60 ${r.deleted ? 'opacity-50' : ''}`}
                >
                  <td className="px-2 py-1.5" style={{ minWidth: 200 }}>
                    {r.deleted ? (
                      <span className="font-mono text-xs text-text-tertiary line-through">
                        {r.productCode}
                      </span>
                    ) : (
                      <SearchableSelect
                        value={r.productCode || null}
                        onChange={(code) => pickProduct(r.key, code)}
                        options={(products.data ?? []).map((p) => ({
                          value: p.code,
                          label: p.code,
                          description: p.name,
                        }))}
                        placeholder={t('tenant.handelMagazyn.newSale.productPlaceholder')}
                        data-testid={`hm-warehouse-product-${index}`}
                      />
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={r.name}
                      disabled={r.deleted}
                      onChange={(e) => setRow(r.key, { name: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={r.quantity}
                      inputMode="decimal"
                      className="text-right"
                      disabled={r.deleted}
                      onChange={(e) => setRow(r.key, { quantity: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={r.unit}
                      className="w-20"
                      disabled={r.deleted}
                      onChange={(e) => setRow(r.key, { unit: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={r.value}
                      inputMode="decimal"
                      className="text-right"
                      disabled={r.deleted}
                      placeholder={t('tenant.handelMagazyn.newWarehouse.valueOptional')}
                      onChange={(e) => setRow(r.key, { value: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    {r.deleted ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('tenant.handelMagazyn.newWarehouse.restorePosition')}
                        onClick={() => setRow(r.key, { deleted: false })}
                        className="h-7 w-7 p-0"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('tenant.handelMagazyn.newSale.removePosition')}
                        disabled={activeRows.length === 1}
                        onClick={() => removeRow(r)}
                        className="h-7 w-7 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <Textarea
          label={t('tenant.handelMagazyn.detail.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
        <Textarea
          label={t('tenant.handelMagazyn.detail.note')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
        />
      </div>

      <ConfirmDialog
        open={confirmIssue}
        onClose={() => setConfirmIssue(false)}
        onConfirm={() => {
          setConfirmIssue(false);
          mutation.mutate(true);
        }}
        variant="primary"
        confirming={mutation.isPending}
        title={t('tenant.handelMagazyn.newWarehouse.confirmTitle')}
        message={t('tenant.handelMagazyn.newWarehouse.confirmBody')}
        confirmLabel={t('tenant.handelMagazyn.newSale.issue')}
      />
    </div>
  );
}

export default HmWarehouseForm;
